import { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import { analytics } from '@play-co/gcinstant';
import { openPopupPromise } from '../../lib/popups/popupOpenClose';
import * as Tutorial from '../../replicant/tutorial';
import StateObserver, { now, user } from '../../StateObserver';
import { TutorialEvent, getTutorialController } from './TutorialController';
import { TeamTutorialFSM } from './fsm/45-TeamsTutorial';
import * as Social from 'src/replicant/social';

export type TutorialAnalytics = {
  $stepIndex: number;
  $stepName: string;
};

const stepTimestamps: Record<string, number> = {};

export async function showTutorial(
  step: TutorialStep,
  analyticsProps: TutorialAnalytics,
) {
  stepTimestamps[step] = now();
  analytics.game.tutorial.TutorialStepStart({
    stepIndex: analyticsProps.$stepIndex,
    stepName: analyticsProps.$stepName,
    tutorialKey: Tutorial.ruleset.version,
  });
  await openPopupPromise('popupTutorialStep', { step });

  // Tutorial step complete
  await completeTutorialStep(step, analyticsProps);
}

/**
 * Same as showTutorial, but without popupTutorialStep popup and TutorialStepStart
 * Use it when you want to show tutorial popup after some other popup
 */
export async function completeTutorialStep(
  step: TutorialStep,
  analyticsProps: TutorialAnalytics,
) {
  const { user } = StateObserver.getState();

  const coinAmount = Tutorial.getters.getTutorialReward(user, step);
  if (coinAmount) {
    await openPopupPromise('popupTutorialReward', { coinAmount });
  }

  await StateObserver.invoke.completeTutorialStep(step);

  // Calculate step elapsed time in seconds for this step
  const startTime = stepTimestamps[step] ?? now();
  const elapsedTime = now() - startTime;
  const stepElapsedSeconds = elapsedTime / 1000;

  analytics.game.tutorial.TutorialStepFinish({
    stepIndex: analyticsProps.$stepIndex,
    stepName: analyticsProps.$stepName,
    tutorialKey: Tutorial.ruleset.version,
    stepElapsedSeconds,
  });
}

/**
 * Send event to finite state machine.
 * @param stateMachine
 * @param event
 * @returns
 */
export async function sendEvent(
  stateMachine: TeamTutorialFSM,
  event: TutorialEvent,
) {
  // Send event to state machine.
  stateMachine.send({ type: event });

  // Get currentState
  const currentState = stateMachine.state;

  // Final state reached.
  if (currentState.matches('final')) {
    stateMachine.stop();
    return !!currentState.changed;
  }

  return !!currentState.changed;
}

/**
 * Wait for machine to reach final state.
 */
export async function waitForFinalState(stateMachine: TeamTutorialFSM) {
  return new Promise<void>((resolve) => {
    let subscription:
      | {
          unsubscribe: () => void;
        }
      | undefined = undefined;
    subscription = stateMachine.subscribe((state) => {
      if (state.matches('final')) {
        subscription?.unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * Setup tutorial state machine.
 */
export function setupTutorialFSM<T extends TeamTutorialFSM>(
  triggers: TutorialEvent[],
  machineFactory: () => T,
) {
  let machine: T | undefined = undefined;

  return {
    handleEvent: async (event: TutorialEvent) => {
      // Check if state machine should be created.
      if (!machine) {
        // Not a trigger event, bail.
        if (!triggers.includes(event)) {
          return false;
        }
      }

      // Create state machine if it doesn't exist.
      const createdMachine = !machine;
      if (!machine) {
        machine = machineFactory();
        machine.start();
      }

      // Send event to state machine and get if it was handled
      const handled = await sendEvent(machine, event);

      // If state machine was created (from trigger event), wait for final state.
      if (createdMachine) {
        return waitForFinalState(machine).then(() => true);
      }

      // Return whether event was handled or not.
      return handled;
    },
    getStatus() {
      return machine?.status;
    },
  };
}

/**
 * The invite challenge was originally in the last state of the basic gameplay FSM,
 * but it is not really part of that tutorial, so it was moved here, to be called from the tutorial handler instead.
 */
export async function showInviteChallengeAfterTutorial() {
  // Only show invite challenge popup if it's running
  if (Social.getters.isInviteChallengeRunning(user(), now())) {
    await openPopupPromise('popupInviteChallenge', {});
  }
  // Send this event to the tutorial controller, so it can trigger the next tutorial, if needed.
  getTutorialController().handleEvent('inviteChallengePresented');
}
